import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  Save,
  Send,
  Trophy,
  type LucideProps,
} from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useTags } from '@/hooks/useTags';
import { SettingsCard } from './SettingsCard';

/** Quando a integração estiver pronta, ligue para exibir o formulário. */
const SHOW_META_CONVERSIONS_FORM = false;

function MetaIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      fill="currentColor"
      aria-hidden
    >
      <path d="M6.915 4.03c-1.968 0-3.683 1.28-4.871 3.113C.704 9.208 0 11.883 0 14.449c0 .706.07 1.369.21 1.973a6.624 6.624 0 0 0 .265.86 5.297 5.297 0 0 0 .371.761c.696 1.159 1.818 1.927 3.593 1.927 1.497 0 2.633-.671 3.965-2.444.76-1.012 1.144-1.626 2.663-4.32l.756-1.339.186-.325c.061.1.121.196.183.3l2.152 3.595c.724 1.21 1.665 2.556 2.47 3.314 1.046.987 1.992 1.22 3.06 1.22 1.075 0 1.876-.355 2.455-.843a3.743 3.743 0 0 0 .81-.973c.542-.939.861-2.127.861-3.745 0-2.72-.681-5.357-2.084-7.45-1.282-1.912-2.957-2.93-4.716-2.93-1.047 0-2.088.467-3.053 1.308-.652.57-1.257 1.29-1.82 2.05-.69-.875-1.335-1.547-1.958-2.056-1.182-.966-2.315-1.303-3.454-1.303zm10.16 2.053c1.147 0 2.188.758 2.992 1.999 1.132 1.748 1.647 4.195 1.647 6.4 0 1.548-.368 2.9-1.839 2.9-.58 0-1.027-.23-1.664-1.004-.496-.601-1.343-1.878-2.832-4.358l-.617-1.028a44.908 44.908 0 0 0-1.255-1.98c.07-.109.141-.224.211-.327 1.12-1.667 2.118-2.602 3.358-2.602zm-10.201.553c1.265 0 2.058.791 2.675 1.446.307.327.737.871 1.234 1.579l-1.02 1.566c-.757 1.163-1.882 3.017-2.837 4.338-1.191 1.649-1.81 1.817-2.486 1.817-.524 0-1.038-.237-1.383-.794-.263-.426-.464-1.13-.464-2.046 0-2.221.63-4.535 1.66-6.088.454-.687.964-1.226 1.533-1.533a2.264 2.264 0 0 1 1.088-.285z" />
    </svg>
  );
}

export interface MetaConversionsSettings {
  isActive: boolean;
  datasetId: string;
  accessToken: string;
  testEventCode: string;
  currency: string;
  purchaseEnabled: boolean;
  purchaseEventName: string;
  qualifiedLeadEnabled: boolean;
  qualifiedLeadEventName: string;
  qualifiedLeadTag: string;
  onlyAdOriginatedLeads: boolean;
}

export const DEFAULT_META_CONVERSIONS_SETTINGS: MetaConversionsSettings = {
  isActive: false,
  datasetId: '',
  accessToken: '',
  testEventCode: '',
  currency: 'BRL',
  purchaseEnabled: true,
  purchaseEventName: 'Purchase',
  qualifiedLeadEnabled: true,
  qualifiedLeadEventName: 'Lead',
  qualifiedLeadTag: 'lead-qualificado',
  onlyAdOriginatedLeads: false,
};

const CURRENCIES = [
  { value: 'BRL', label: 'BRL — Real brasileiro' },
  { value: 'USD', label: 'USD — Dólar americano' },
  { value: 'EUR', label: 'EUR — Euro' },
];

const LABEL_CLASS = 'text-sm font-medium text-[#0F172A] dark:text-[#FAFAFA]';

const INPUT_CLASS =
  'h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A]';

const HINT_CLASS = 'text-xs text-[#64748B] dark:text-[#A1A1AA]';

const NESTED_PANEL_CLASS =
  'rounded-xl border border-[#E5E7EB] bg-white p-4 dark:border-[#3F3F464D] dark:bg-[#18181B]';

const ICON_BADGE_CLASS =
  'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#EEF4FF] text-[#2563EB] dark:bg-blue-500/20 dark:text-[#60A5FA]';

type FeedbackMessage = { type: 'success' | 'error' | 'pending'; text: string };

function Field({
  htmlFor,
  label,
  hint,
  children,
}: {
  htmlFor: string;
  label: string;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={htmlFor} className={LABEL_CLASS}>
        {label}
      </Label>
      {children}
      {hint ? <p className={HINT_CLASS}>{hint}</p> : null}
    </div>
  );
}

function EventBlock({
  icon: Icon,
  title,
  trigger,
  enabled,
  onToggle,
  children,
}: {
  icon: ComponentType<LucideProps>;
  title: string;
  trigger: string;
  enabled: boolean;
  onToggle: (checked: boolean) => void;
  children: ReactNode;
}) {
  return (
    <div className={NESTED_PANEL_CLASS}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3">
          <div className={ICON_BADGE_CLASS}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[#0F172A] dark:text-[#FAFAFA]">{title}</p>
            <p className="mt-0.5 text-xs text-[#64748B] dark:text-[#A1A1AA] sm:text-sm">{trigger}</p>
          </div>
        </div>

        <Switch
          checked={enabled}
          onCheckedChange={onToggle}
          aria-label={`Ativar evento ${title}`}
          activeColor="data-[state=checked]:bg-[#2563EB]"
        />
      </div>

      {enabled ? (
        <div className="mt-4 grid gap-4 border-t border-[#E5E7EB] pt-4 dark:border-[#3F3F464D] sm:grid-cols-2">
          {children}
        </div>
      ) : null}
    </div>
  );
}

const MetaConversionsCard = () => {
  const { tags, loading: tagsLoading } = useTags();
  const [settings, setSettings] = useState<MetaConversionsSettings>(
    DEFAULT_META_CONVERSIONS_SETTINGS
  );
  const [message, setMessage] = useState<FeedbackMessage | null>(null);

  const update = <K extends keyof MetaConversionsSettings>(
    key: K,
    value: MetaConversionsSettings[K]
  ) => {
    setSettings((current) => ({ ...current, [key]: value }));
    setMessage(null);
  };

  const isConfigured = settings.datasetId.trim() !== '' && settings.accessToken.trim() !== '';

  const tagOptions = useMemo(() => {
    const activeNames = tags.filter((tag) => tag.is_active !== false).map((tag) => tag.name);
    const configured = settings.qualifiedLeadTag.trim();
    const alreadyListed = activeNames.some(
      (name) => name.toLowerCase() === configured.toLowerCase()
    );

    const options = activeNames.map((name) => ({ name, missing: false }));
    if (configured && !alreadyListed) {
      options.unshift({ name: configured, missing: true });
    }
    return options;
  }, [tags, settings.qualifiedLeadTag]);

  const validate = (): string | null => {
    if (!settings.datasetId.trim()) return 'Informe o ID do dataset ou do pixel.';
    if (!settings.accessToken.trim()) return 'Informe o token de acesso da Meta.';
    if (settings.purchaseEnabled && !settings.purchaseEventName.trim()) {
      return 'Informe o nome do evento de conversão.';
    }
    if (settings.qualifiedLeadEnabled && !settings.qualifiedLeadEventName.trim()) {
      return 'Informe o nome do evento de lead qualificado.';
    }
    if (settings.qualifiedLeadEnabled && !settings.qualifiedLeadTag.trim()) {
      return 'Escolha a tag que dispara o evento de lead qualificado.';
    }
    if (!settings.purchaseEnabled && !settings.qualifiedLeadEnabled) {
      return 'Ative pelo menos um evento para enviar à Meta.';
    }
    return null;
  };

  // TODO(backend): persistir em account_meta_integrations quando a Edge Function de envio existir.
  const handleSave = () => {
    const error = validate();
    if (error) {
      setMessage({ type: 'error', text: error });
      return;
    }
    setMessage({
      type: 'pending',
      text: 'Configuração validada. O armazenamento e o envio de eventos serão ativados quando a integração for concluída.',
    });
  };

  const handleTest = () => {
    setMessage({
      type: 'pending',
      text: 'O envio de evento de teste depende da conclusão da integração com a Meta.',
    });
  };

  return (
    <SettingsCard className="p-5">
      <div className="flex min-w-0 items-start gap-4">
        <div className={ICON_BADGE_CLASS}>
          <MetaIcon className="h-5 w-5" />
        </div>
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold text-[#0F172A] dark:text-[#FAFAFA]">
              Meta — Conversões
            </h3>
            <span className="rounded-md bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-600 dark:bg-red-500/20 dark:text-red-400">
              Em breve
            </span>
          </div>
          <p className="text-sm text-[#64748B] dark:text-[#A1A1AA]">
            Envie conversões do CRM para a Conversions API da Meta e permita que campanhas do
            Facebook e Instagram otimizem por quem realmente fecha negócio.
          </p>
        </div>
      </div>

      {SHOW_META_CONVERSIONS_FORM ? (
        <>
          <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex shrink-0 items-center gap-3 self-start rounded-xl border border-[#E5E7EB] bg-white px-4 py-3 dark:border-[#3F3F464D] dark:bg-[#18181B] sm:ml-auto">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#0F172A] dark:text-[#FAFAFA]">
                  Integração ativa
                </p>
                <p className="text-xs text-[#64748B] dark:text-[#A1A1AA]">Liberar envio de eventos</p>
              </div>
              <Switch
                checked={settings.isActive}
                onCheckedChange={(checked) => update('isActive', checked)}
                disabled={!isConfigured}
                aria-label="Ativar integração com a Meta"
                activeColor="data-[state=checked]:bg-[#2563EB]"
              />
            </div>
          </div>

          <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-3 text-sm font-medium text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Integração em implantação. As configurações abaixo já refletem o formato final, mas
              ainda não são salvas nem geram envio de eventos para a Meta.
            </span>
          </div>

          {message ? (
            <div
              className={`mt-4 rounded-xl border px-3.5 py-3 text-sm font-medium ${
                message.type === 'error'
                  ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300'
                  : message.type === 'success'
                    ? 'border-green-200 bg-green-50 text-green-700 dark:border-green-500/30 dark:bg-green-500/10 dark:text-green-300'
                    : 'border-[#C9D8FF] bg-[#F5F8FF] text-[#2563EB] dark:border-blue-500/30 dark:bg-blue-500/10 dark:text-[#60A5FA]'
              }`}
            >
              {message.text}
            </div>
          ) : null}

          <div className="mt-5 space-y-3">
            <div>
              <h4 className="text-sm font-semibold text-[#0F172A] dark:text-[#FAFAFA]">
                Credenciais
              </h4>
              <p className={`mt-1 ${HINT_CLASS}`}>
                Encontre esses dados no Gerenciador de Eventos da Meta, dentro da fonte de dados
                usada pelas suas campanhas.
              </p>
            </div>

            <div className={`${NESTED_PANEL_CLASS} grid gap-4 sm:grid-cols-2`}>
              <Field
                htmlFor="meta-dataset-id"
                label="ID do dataset ou pixel"
                hint="Identificador numérico da fonte de dados que receberá os eventos."
              >
                <Input
                  id="meta-dataset-id"
                  value={settings.datasetId}
                  onChange={(event) => update('datasetId', event.target.value)}
                  placeholder="1234567890123456"
                  inputMode="numeric"
                  autoComplete="off"
                  className={INPUT_CLASS}
                />
              </Field>

              <Field
                htmlFor="meta-access-token"
                label="Token de acesso"
                hint="Token de usuário de sistema com permissão para enviar eventos."
              >
                <Input
                  id="meta-access-token"
                  type="password"
                  value={settings.accessToken}
                  onChange={(event) => update('accessToken', event.target.value)}
                  placeholder="EAAG..."
                  autoComplete="new-password"
                  className={INPUT_CLASS}
                />
              </Field>

              <Field
                htmlFor="meta-test-event-code"
                label="Código de evento de teste (opcional)"
                hint="Preencha para validar os envios na aba Eventos de Teste antes de ir ao ar."
              >
                <Input
                  id="meta-test-event-code"
                  value={settings.testEventCode}
                  onChange={(event) => update('testEventCode', event.target.value)}
                  placeholder="TEST12345"
                  autoComplete="off"
                  className={INPUT_CLASS}
                />
              </Field>

              <Field
                htmlFor="meta-currency"
                label="Moeda dos valores"
                hint="Usada no valor da venda enviado junto com a conversão."
              >
                <Select
                  value={settings.currency}
                  onValueChange={(value) => update('currency', value)}
                >
                  <SelectTrigger id="meta-currency" className={`w-full ${INPUT_CLASS}`}>
                    <SelectValue placeholder="Selecione a moeda" />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((currency) => (
                      <SelectItem
                        key={currency.value}
                        value={currency.value}
                        className="normal-case"
                      >
                        {currency.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </div>

          <div className="mt-5 space-y-3">
            <div>
              <h4 className="text-sm font-semibold text-[#0F172A] dark:text-[#FAFAFA]">
                Eventos enviados
              </h4>
              <p className={`mt-1 ${HINT_CLASS}`}>
                Cada evento é disparado automaticamente por uma ação dentro da Rolt. O nome precisa
                ser o mesmo cadastrado na Meta.
              </p>
            </div>

            <EventBlock
              icon={Trophy}
              title="Conversão"
              trigger="Disparado quando uma oportunidade é marcada como ganha no CRM."
              enabled={settings.purchaseEnabled}
              onToggle={(checked) => update('purchaseEnabled', checked)}
            >
              <Field
                htmlFor="meta-purchase-event-name"
                label="Nome do evento na Meta"
                hint="Purchase é o evento padrão de venda concluída."
              >
                <Input
                  id="meta-purchase-event-name"
                  value={settings.purchaseEventName}
                  onChange={(event) => update('purchaseEventName', event.target.value)}
                  placeholder="Purchase"
                  autoComplete="off"
                  className={INPUT_CLASS}
                />
              </Field>

              <div className="flex items-start gap-2.5 rounded-xl border border-[#C9D8FF] bg-[#F5F8FF] px-3.5 py-3 text-xs font-medium text-[#2563EB] dark:border-blue-500/30 dark:bg-blue-500/10 dark:text-[#60A5FA]">
                <Info className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  O valor e o produto da venda são enviados a partir dos dados preenchidos ao marcar
                  a oportunidade como ganha.
                </span>
              </div>
            </EventBlock>

            <EventBlock
              icon={CheckCircle2}
              title="Lead qualificado"
              trigger="Disparado quando a tag escolhida é adicionada a um lead."
              enabled={settings.qualifiedLeadEnabled}
              onToggle={(checked) => update('qualifiedLeadEnabled', checked)}
            >
              <Field
                htmlFor="meta-qualified-event-name"
                label="Nome do evento na Meta"
                hint="Use Lead ou um evento personalizado já criado na Meta."
              >
                <Input
                  id="meta-qualified-event-name"
                  value={settings.qualifiedLeadEventName}
                  onChange={(event) => update('qualifiedLeadEventName', event.target.value)}
                  placeholder="Lead"
                  autoComplete="off"
                  className={INPUT_CLASS}
                />
              </Field>

              <Field
                htmlFor="meta-qualified-tag"
                label="Tag que dispara o evento"
                hint={
                  tagsLoading
                    ? 'Carregando tags da conta...'
                    : 'Ao adicionar essa tag no lead, o evento é enviado uma única vez.'
                }
              >
                <Select
                  value={settings.qualifiedLeadTag}
                  onValueChange={(value) => update('qualifiedLeadTag', value)}
                >
                  <SelectTrigger id="meta-qualified-tag" className={`w-full ${INPUT_CLASS}`}>
                    <SelectValue placeholder="Selecione uma tag" />
                  </SelectTrigger>
                  <SelectContent>
                    {tagOptions.map((option) => (
                      <SelectItem key={option.name} value={option.name}>
                        {option.missing ? `${option.name} (será criada)` : option.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </EventBlock>
          </div>

          <div className="mt-5 space-y-3">
            <div>
              <h4 className="text-sm font-semibold text-[#0F172A] dark:text-[#FAFAFA]">
                Comportamento de envio
              </h4>
            </div>

            <div className={`${NESTED_PANEL_CLASS} flex items-start justify-between gap-4`}>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#0F172A] dark:text-[#FAFAFA]">
                  Enviar apenas leads originados de anúncio
                </p>
                <p className={`mt-0.5 ${HINT_CLASS}`}>
                  Desativado, todas as conversões são enviadas e a própria Meta descarta as que não
                  pertencem a nenhuma campanha. Ativar tende a reduzir os resultados atribuídos.
                </p>
              </div>
              <Switch
                checked={settings.onlyAdOriginatedLeads}
                onCheckedChange={(checked) => update('onlyAdOriginatedLeads', checked)}
                aria-label="Enviar apenas leads originados de anúncio"
                activeColor="data-[state=checked]:bg-[#2563EB]"
              />
            </div>
          </div>

          <div className="mt-5 flex flex-col gap-3 border-t border-[#E5E7EB] pt-5 dark:border-[#3F3F464D] sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={handleTest}
              disabled={!isConfigured}
              className="rounded-xl border-[#E5E7EB] text-sm text-[#0F172A] transition-colors duration-200 hover:bg-[#F8FAFC] disabled:opacity-50 dark:border-[#3F3F46] dark:text-[#FAFAFA] dark:hover:bg-[#3F3F46]/50"
            >
              <Send className="mr-2 h-4 w-4" />
              Enviar evento de teste
            </Button>

            <Button
              type="button"
              onClick={handleSave}
              className="rounded-xl bg-[#2563EB] px-4 text-sm text-white shadow-sm transition-colors duration-200 hover:bg-[#1D4ED8]"
            >
              <Save className="mr-2 h-4 w-4" />
              Salvar configurações
            </Button>
          </div>
        </>
      ) : null}
    </SettingsCard>
  );
};

export default MetaConversionsCard;
